import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useOrganizations,
  useCreateOrganization,
  useOrgMembers,
  useAddMember,
  useRemoveMember,
} from '../hooks/useOrganizations';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default function Organizations() {
  const user = useAuthStore((s) => s.user);
  const { data: orgs, isLoading } = useOrganizations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = orgs?.find((o) => o.id === selectedId) ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <span className="text-sm font-medium text-foreground">Organizations</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2">Organizations</h2>
          <p className="text-muted-foreground">
            Create teams and manage who has access.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-[1fr_1.4fr]">
          <div className="space-y-4">
            <CreateOrgForm />
            {isLoading ? (
              <LoadingSkeleton className="h-32" />
            ) : (orgs?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                No organizations yet.
              </p>
            ) : (
              <div className="space-y-2">
                {orgs!.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setSelectedId(o.id)}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${
                      selectedId === o.id
                        ? 'border-primary bg-white'
                        : 'border-transparent bg-white/60 hover:bg-white'
                    }`}
                  >
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">
                      /{o.slug} · {o.memberCount} member
                      {o.memberCount === 1 ? '' : 's'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {selected ? (
              <MembersPanel orgId={selected.id} orgName={selected.name} />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Select an organization to manage its members.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function CreateOrgForm() {
  const create = useCreateOrganization();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onNameChange(v: string) {
    setName(v);
    if (!slugEdited) setSlug(slugify(v));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate(
      { name, slug, description: undefined },
      {
        onSuccess: () => {
          setName('');
          setSlug('');
          setSlugEdited(false);
        },
        onError: (err: any) =>
          setError(err.response?.data?.detail || 'Could not create organization'),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">New organization</CardTitle>
        <CardDescription>Create a team to share work.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              required
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-slug">Slug</Label>
            <Input
              id="org-slug"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugEdited(true);
              }}
              required
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              placeholder="my-team"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={create.isPending || !name || !slug}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function MembersPanel({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { data: members, isLoading } = useOrgMembers(orgId);
  const addMember = useAddMember(orgId);
  const removeMember = useRemoveMember(orgId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('MEMBER');
  const [error, setError] = useState<string | null>(null);

  function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    addMember.mutate(
      { email, role },
      {
        onSuccess: () => setEmail(''),
        onError: (err: any) =>
          setError(err.response?.data?.detail || 'Could not add member'),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{orgName} · Members</CardTitle>
        <CardDescription>Add teammates by email and set their role.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={add} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 space-y-1.5 min-w-[180px]">
            <Label htmlFor="member-email">Email</Label>
            <Input
              id="member-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="member-role">Role</Label>
            <select
              id="member-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="MEMBER">Member</option>
              <option value="ADMIN">Admin</option>
              <option value="VIEWER">Viewer</option>
            </select>
          </div>
          <Button type="submit" disabled={addMember.isPending}>
            {addMember.isPending ? 'Adding…' : 'Add'}
          </Button>
        </form>
        {error && <p className="text-sm text-red-600">{error}</p>}

        {isLoading ? (
          <LoadingSkeleton className="h-24" />
        ) : (
          <div className="divide-y rounded-lg border">
            {(members ?? []).map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3">
                <div>
                  <div className="text-sm font-medium">
                    {m.email ?? m.userId}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.role}
                    {m.role === 'OWNER' ? ' · owner' : ''}
                  </div>
                </div>
                {m.role !== 'OWNER' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={removeMember.isPending}
                    onClick={() => removeMember.mutate(m.userId)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
